/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, useCallback, useRef } from "react";

interface UseMeditationSoundReturn {
  isAudioPlaying: boolean;
  toggleMeditationSound: () => void;
  stopMeditationSound: () => void;
}

export function useMeditationSound(): UseMeditationSoundReturn {
  const [isAudioPlaying, setIsAudioPlaying] = useState<boolean>(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const oscillatorsRef = useRef<OscillatorNode[]>([]);
  const gainNodeRef = useRef<GainNode | null>(null);

  const stopMeditationSound = useCallback(() => {
    oscillatorsRef.current.forEach((osc) => {
      try {
        osc.stop();
        osc.disconnect();
      } catch (e) {}
    });
    if (gainNodeRef.current) {
      try {
        gainNodeRef.current.disconnect();
      } catch (e) {}
    }
    oscillatorsRef.current = [];
    gainNodeRef.current = null;
    setIsAudioPlaying(false);
  }, []);

  const toggleMeditationSound = useCallback(() => {
    if (isAudioPlaying) {
      stopMeditationSound();
    } else {
      try {
        const ctx = audioCtxRef.current || new (window.AudioContext || (window as any).webkitAudioContext)();
        if (!audioCtxRef.current) audioCtxRef.current = ctx;

        // Resume context if suspended (browser security rules)
        if (ctx.state === "suspended") {
          ctx.resume();
        }

        // Create main oscillator (Deep Warm Sine drone)
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(132, ctx.currentTime); // C2 chord fundamental frequency

        // Create a secondary harmonic oscillator (Solfeggio 528Hz - Transformation & Miracles)
        const osc2 = ctx.createOscillator();
        osc2.type = "sine";
        osc2.frequency.setValueAtTime(528, ctx.currentTime); // Healing frequency

        // Create Gain node for gentle volume controls
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.0, ctx.currentTime);
        // Fade-in volume smoothly to avoid pops
        gain.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 3.0);

        osc.connect(gain);
        osc2.connect(gain);
        gain.connect(ctx.destination);

        osc.start();
        osc2.start();

        oscillatorsRef.current = [osc, osc2];
        gainNodeRef.current = gain;
        setIsAudioPlaying(true);
      } catch (err) {
        console.error("Web Audio Synth failed to start", err);
      }
    }
  }, [isAudioPlaying, stopMeditationSound]);

  // Turn off synth if meditation mode is explicitly turned off
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      stopMeditationSound();
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
      }
    };
  }, [stopMeditationSound]);

  return {
    isAudioPlaying,
    toggleMeditationSound,
    stopMeditationSound,
  };
}